import { useEffect, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';

const ROLLE_OPTIONS = [
  { value: 'vertrieb', label: 'Vertrieb' },
  { value: 'fuehrungskraft', label: 'Führungskraft' },
  { value: 'admin', label: 'Administrator:in' },
];

const EMPTY = {
  vorname: '',
  nachname: '',
  email: '',
  rolle: 'vertrieb',
  password: '',
  passwordConfirm: '',
  newPassword: '',
};

export default function UserFormModal({ open, onClose, onSaved, user }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        vorname: user.vorname ?? '',
        nachname: user.nachname ?? '',
        email: user.email ?? '',
        rolle: user.rolle || 'vertrieb',
        password: '',
        passwordConfirm: '',
        newPassword: '',
      });
    } else {
      setForm(EMPTY);
    }
    setError(null);
  }, [open, user, isEdit]);

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const fullName = `${form.vorname} ${form.nachname}`.trim();

      if (isEdit) {
        // Stammdaten aktualisieren — Passwort wird separat behandelt,
        // weil PB sonst oldPassword verlangen würde.
        await pb.collection('users').update(user.id, {
          vorname: form.vorname,
          nachname: form.nachname,
          name: fullName,
          rolle: form.rolle,
        });

        if (form.newPassword) {
          if (form.newPassword.length < 8) {
            throw new Error('Neues Passwort muss mindestens 8 Zeichen lang sein.');
          }
          await pb.send('/api/admin/reset-password', {
            method: 'POST',
            body: { userId: user.id, newPassword: form.newPassword },
          });
        }
      } else {
        if (!form.password || form.password.length < 8) {
          throw new Error('Passwort muss mindestens 8 Zeichen lang sein.');
        }
        if (form.password !== form.passwordConfirm) {
          throw new Error('Passwörter stimmen nicht überein.');
        }
        await pb.collection('users').create({
          email: form.email,
          password: form.password,
          passwordConfirm: form.passwordConfirm,
          vorname: form.vorname,
          nachname: form.nachname,
          name: fullName,
          rolle: form.rolle,
          emailVisibility: true,
        });
      }

      onSaved?.();
      onClose();
    } catch (err) {
      // PocketBase gibt Feld-Validierungsfehler in err.response?.data zurück.
      const fieldErrors = err?.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const details = Object.entries(fieldErrors)
          .map(([field, info]) => `${field}: ${info?.message || JSON.stringify(info)}`)
          .join('; ');
        setError(details || err?.message || 'Speichern fehlgeschlagen.');
      } else {
        setError(err?.message || 'Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Vorname *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.vorname}
              onChange={handleChange('vorname')}
            />
          </div>

          <div>
            <label className="label">Nachname *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.nachname}
              onChange={handleChange('nachname')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">E-Mail *</label>
            <input
              type="email"
              required
              disabled={isEdit}
              className="input mt-1 disabled:bg-slate-100"
              value={form.email}
              onChange={handleChange('email')}
            />
            {isEdit && (
              <p className="mt-1 text-xs text-slate-500">
                E-Mail kann nachträglich nicht geändert werden.
              </p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="label">Rolle *</label>
            <select
              required
              className="input mt-1"
              value={form.rolle}
              onChange={handleChange('rolle')}
            >
              {ROLLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {!isEdit && (
            <>
              <div className="sm:col-span-2">
                <label className="label">Passwort *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  className="input mt-1"
                  value={form.password}
                  onChange={handleChange('password')}
                />
                <p className="mt-1 text-xs text-slate-500">Mindestens 8 Zeichen.</p>
              </div>
              <div className="sm:col-span-2">
                <label className="label">Passwort bestätigen *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  className="input mt-1"
                  value={form.passwordConfirm}
                  onChange={handleChange('passwordConfirm')}
                />
              </div>
            </>
          )}

          {isEdit && (
            <div className="sm:col-span-2">
              <label className="label">Neues Passwort (optional)</label>
              <input
                type="password"
                minLength={8}
                placeholder="leer lassen für unverändert"
                className="input mt-1"
                value={form.newPassword}
                onChange={handleChange('newPassword')}
              />
              <p className="mt-1 text-xs text-slate-500">
                Wird über den Admin-Passwort-Reset-Endpoint gesetzt — überschreibt
                das bisherige Passwort sofort.
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
